/**
 * MarketMap Component
 *
 * Treemap visualization of top movers and market overviews.
 * Tile size reflects absolute % change magnitude.
 */

import { FC, useState, useCallback } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import { colors, spacing, fontStyles } from '../../styles';
import {
  getTopMovers,
  MarketMapPeriod,
  getMarketOverview,
  MarketOverviewView,
  MarketOverviewStock,
  TopMoverStock,
  TopMoversUniverse,
} from '../../apis/backend';
import * as stores from '../../stores';
import { CustomizedContent, CustomizedContentBlurred, CustomTooltip, CustomContentProps } from './shared';

export interface MarketMapProps {
  initialPeriod?: MarketMapPeriod;
}

// Period options for the selector
const PERIODS: { value: MarketMapPeriod; label: string }[] = [
  { value: '1d', label: '1D' },
  { value: '1w', label: '1W' },
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '6m', label: '6M' },
  { value: 'ytd', label: 'YTD' },
  { value: '1y', label: '1Y' },
  { value: '5y', label: '5Y' },
];

// Dropdown selection type - view modes only
type DropdownValue =
  | 'top_movers'
  | 'top_movers_sp500'
  | 'top_movers_international'
  | 'top_movers_crypto'
  | 'sectors'
  | 'global'
  | 'indexes'
  | 'industries';

// Map dropdown values to top movers universe
const DROPDOWN_TO_UNIVERSE: Partial<Record<DropdownValue, TopMoversUniverse>> = {
  top_movers: 'all',
  top_movers_sp500: 'sp500',
  top_movers_international: 'international',
  top_movers_crypto: 'crypto',
};

// View options for the dropdown
const VIEW_OPTIONS: { value: DropdownValue; label: string }[] = [
  { value: 'top_movers', label: 'Top Movers' },
  { value: 'top_movers_sp500', label: 'Top Movers · S&P 500' },
  { value: 'top_movers_international', label: 'Top Movers · International' },
  { value: 'top_movers_crypto', label: 'Top Movers · Crypto' },
  { value: 'sectors', label: 'Sector Breakdown' },
  { value: 'industries', label: 'Industry Breakdown' },
  { value: 'global', label: 'Global Markets' },
  { value: 'indexes', label: 'Major Indexes' },
];

// Check if a dropdown value is an overview mode (not top movers)
const isOverviewMode = (value: DropdownValue): boolean => {
  return ['sectors', 'global', 'indexes', 'industries'].includes(value);
};

// Check if a dropdown value is a top movers mode
const isTopMoversMode = (value: DropdownValue): boolean => {
  return value in DROPDOWN_TO_UNIVERSE;
};

// Skeleton loader for treemap area only (keeps controls visible)
const TreemapSkeleton: FC = () => (
  <div
    css={css`
      width: 100%;
      height: 350px;
      background: ${colors.cloud};
      border-radius: 8px;
      animation: pulse 1.5s ease-in-out infinite;

      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }

      html[data-theme='dark'] & {
        background: ${colors.tungsten};
      }
    `}
  />
);

// Full skeleton loader (for initial page load)
export const MarketMapSkeleton: FC = () => {
  return (
    <div
      css={css`
        margin-bottom: ${spacing.deci};
      `}
    >
      <div
        css={css`
          width: 100%;
          height: 40px;
          background: ${colors.cloud};
          border-radius: 8px;
          margin-bottom: ${spacing.deci};
          margin-top: ${spacing.deci};
          animation: pulse 1.5s ease-in-out infinite;

          @keyframes pulse {
            0%,
            100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }

          html[data-theme='dark'] & {
            background: ${colors.tungsten};
          }
        `}
      />
      <TreemapSkeleton />
      <div
        css={css`
          display: flex;
          justify-content: center;
          margin-top: ${spacing.deci};
          gap: ${spacing.centi};
        `}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div
            key={i}
            css={css`
              width: 40px;
              height: 28px;
              background: ${colors.cloud};
              border-radius: 24px;
              animation: pulse 1.5s ease-in-out infinite;

              html[data-theme='dark'] & {
                background: ${colors.tungsten};
              }
            `}
          />
        ))}
      </div>
    </div>
  );
};

// Check if a view requires subscription (all except top_movers variants)
const isPremiumView = (value: DropdownValue): boolean => {
  return !(value in DROPDOWN_TO_UNIVERSE);
};

export const MarketMap: FC<MarketMapProps> = ({ initialPeriod = '1w' }) => {
  const history = useHistory();
  const [period, setPeriod] = useState<MarketMapPeriod>(initialPeriod);
  const [selection, setSelection] = useState<DropdownValue>('top_movers');
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const hasActiveSubscription = !!purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  const isOverview = isOverviewMode(selection);
  const isTopMovers = isTopMoversMode(selection);
  const isLockedView = isPremiumView(selection) && !hasActiveSubscription;
  const universe = DROPDOWN_TO_UNIVERSE[selection] || 'all';

  // Fetch market data with React Query - separate queries for different data types
  const topMoversQuery = useQuery(
    ['market-map', 'top-movers', period, universe],
    () => getTopMovers(period, 'all', 24, universe),
    {
      keepPreviousData: true,
      enabled: isTopMovers,
    },
  );

  const overviewQuery = useQuery(
    ['market-map', 'overview', period, selection],
    () => getMarketOverview(period, selection as MarketOverviewView),
    { keepPreviousData: true, enabled: isOverview },
  );

  // Get the active query's state
  const activeQuery = isOverview ? overviewQuery : topMoversQuery;
  const { isLoading, isFetching } = activeQuery;
  const stocks = (activeQuery.data?.stocks || []) as (MarketOverviewStock | TopMoverStock)[];

  const handleCellClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (nodeData: any) => {
      if (nodeData?.symbol) {
        history.push(`/symbol/${nodeData.symbol}`);
      }
    },
    [history],
  );

  // Transform stocks to treemap format - size by absolute % change
  // Filter out stocks with null change_percent
  const treemapData = stocks
    .filter((stock) => stock.change_percent != null)
    .map((stock) => ({
      name: stock.name,
      symbol: stock.symbol,
      display_name: 'display_name' in stock ? stock.display_name : undefined,
      size: Math.abs(stock.change_percent) + 1, // +1 to ensure minimum size
      change_percent: stock.change_percent,
      market_cap: stock.market_cap,
    }));

  return (
    <div
      css={css`
        margin-bottom: ${spacing.deci};
      `}
    >
      {/* Unified Dropdown - combines view modes and sector filters */}
      <div
        css={css`
          margin-top: ${spacing.deci};
          margin-bottom: ${spacing.deci};
        `}
      >
        <select
          value={selection}
          onChange={(e) => {
            const newValue = e.target.value as DropdownValue;
            setSelection(newValue);
          }}
          css={css`
            width: 100%;
            padding: ${spacing.centi} ${spacing.deci};
            ${fontStyles.cat}
            border: 0.5px solid ${colors.cloud};
            border-radius: 8px;
            background: ${colors.snow};
            color: ${colors.petrolBlue};
            cursor: pointer;
            appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right ${spacing.deci} center;
            padding-right: ${spacing.kilo};

            &:focus {
              outline: none;
              border-color: ${colors.petrolBlue};
            }

            html[data-theme='dark'] & {
              background-color: ${colors.liquorice};
              color: ${colors.sugar};
              border-color: rgba(80, 78, 75, 0.3);
              background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23aaa' d='M6 8L1 3h10z'/%3E%3C/svg%3E");

              &:focus {
                border-color: ${colors.skyBlue};
              }
            }
          `}
        >
          {VIEW_OPTIONS.map(({ value, label }) => {
            const isLocked = isPremiumView(value) && !hasActiveSubscription;
            return (
              <option key={value} value={value}>
                {label}
                {isLocked ? ' · PRO' : ''}
              </option>
            );
          })}
        </select>
      </div>

      {/* Treemap */}
      <div
        css={css`
          width: 100%;
          height: 350px;
          border-radius: 8px;
          overflow: hidden;
          position: relative;
        `}
      >
        {isLoading ? (
          <TreemapSkeleton />
        ) : treemapData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={350}>
              <Treemap
                data={treemapData}
                dataKey="size"
                aspectRatio={4 / 3}
                stroke="#fff"
                content={(props: CustomContentProps) =>
                  isLockedView ? <CustomizedContentBlurred {...props} /> : <CustomizedContent {...props} />
                }
                onClick={isLockedView ? undefined : handleCellClick}
                isAnimationActive={false}
              >
                {!isLockedView && <Tooltip content={<CustomTooltip />} />}
              </Treemap>
            </ResponsiveContainer>
            {/* Loading overlay for background refetch */}
            {isFetching && (
              <div
                css={css`
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background: rgba(255, 255, 255, 0.5);
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  html[data-theme='dark'] & {
                    background: rgba(0, 0, 0, 0.5);
                  }
                `}
              />
            )}
            {isLockedView && (
              <div
                onClick={() => stores.showPaymentModalByPreference('ideas_page_market_map')}
                css={css`
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  cursor: pointer;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: ${spacing.micro};
                    background: ${colors.sugar};
                    border: 1.5px solid ${colors.petrolBlue}50;
                    padding: ${spacing.centi} ${spacing.deci};
                    border-radius: 12px;

                    html[data-theme='dark'] & {
                      background: ${colors.liquorice};
                    }
                  `}
                >
                  <span
                    css={css`
                      font-size: 18px;
                    `}
                  >
                    🔒
                  </span>
                  <span
                    css={css`
                      ${fontStyles.flea}
                      color: ${colors.petrolBlue};
                      font-weight: 500;
                      white-space: nowrap;
                      text-align: center;
                    `}
                  >
                    Unlock
                    <br />
                    with Pro
                  </span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100%;
              color: ${colors.silver};
              ${fontStyles.dog}

              html[data-theme='dark'] & {
                color: ${colors.tungsten};
              }
            `}
          >
            No market data available
          </div>
        )}
      </div>

      {/* Period Selector */}
      <div
        css={css`
          display: flex;
          justify-content: center;
          margin-top: ${spacing.deci};
          gap: ${spacing.centi};

          @media (max-width: 400px) {
            gap: ${spacing.milli};
          }
        `}
        role="radiogroup"
      >
        {PERIODS.map(({ value, label }) => {
          const isChecked = period === value;
          return (
            <label
              key={value}
              css={css`
                position: relative;
                border: var(--border-color) 2px solid;
                color: var(--secondary-text);
                background-color: ${colors.snow};
                padding: ${spacing.milli} ${spacing.centi};
                min-width: 40px;
                text-align: center;
                border-radius: 24px;
                cursor: pointer;
                -webkit-tap-highlight-color: transparent;
                ${fontStyles.fairyfly};
                transition: all 0.2s;

                @media (max-width: 400px) {
                  min-width: 32px;
                  padding: ${spacing.micro} ${spacing.milli};
                }

                &:hover {
                  background-color: rgba(0, 0, 0, 0.05);
                }

                html[data-theme='dark'] & {
                  background-color: ${colors.liquorice};
                  color: ${isChecked ? colors.skyBlue : colors.sugar};
                  border-color: ${isChecked ? colors.skyBlue : 'var(--border-color)'};
                  &:hover {
                    background-color: rgba(255, 255, 255, 0.05);
                  }
                }

                ${isChecked &&
                `
                  color: ${colors.petrolBlue};
                  border-color: ${colors.petrolBlue};
                `}
              `}
            >
              {label}
              <input
                type="radio"
                value={value}
                checked={isChecked}
                onChange={() => setPeriod(value)}
                css={css`
                  position: absolute;
                  visibility: hidden;
                `}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default MarketMap;
